// Gold purity factors — industry standard values used for valuation.
// 18K is treated as 0.76 (not 18/24 = 0.75) per business requirement.
// 22K is treated as 0.92 per business requirement.
export const GOLD_PURITY_FACTORS = {
  24: 0.9999,
  22: 0.92,
  18: 0.76,
  14: 0.5833,
};

/**
 * Exchange rate adjustment constant.
 * The raw API/DB value is reduced by this amount to arrive at the
 * effective working rate used for all calculations and displays.
 * Change this single value to update the adjustment everywhere.
 */
export const EXCHANGE_RATE_ADJUSTMENT = 3;

/**
 * Converts a raw usd_to_inr value from the DB/API into the effective
 * working exchange rate used across the entire application.
 *
 *   effectiveRate = floor(rawRate - EXCHANGE_RATE_ADJUSTMENT)
 *
 * Returns 0 if the raw value is falsy or non-positive.
 *
 * @param {number|string} rawRate  - raw usd_to_inr value from the API
 * @returns {number}               - effective integer exchange rate (INR per 1 USD)
 */
export const applyExchangeRate = (rawRate) => {
  const r = parseFloat(rawRate);
  if (!r || r <= 0) return 0;
  return Math.floor(r - EXCHANGE_RATE_ADJUSTMENT);
};

/**
 * Returns the purity factor for a given karat value.
 * Accepts numeric (18, 22, 24) or string ('18KT', '22KT', '24KT').
 * Falls back to 18K factor (0.76) if unrecognised.
 */
export const getGoldPurityFactor = (purity) => {
  const karat = parseInt(String(purity).replace(/[^0-9]/g, ''), 10);
  return GOLD_PURITY_FACTORS[karat] ?? GOLD_PURITY_FACTORS[18];
};

// ── Stone weight utilities ──────────────────────────────────────────────────

/**
 * Convert a single stone's weight to grams based on its unit.
 *   carat  → weight × 0.2  (1 ct = 0.2 g)
 *   number → 0             (count only, no gram deduction)
 *   gram   → direct        (default)
 *
 * @param {number|string} weight  - raw weight value
 * @param {string}        unit    - 'carat' | 'number' | 'gram' (default)
 * @returns {number}              - weight in grams
 */
export const stoneWeightToGrams = (weight, unit) => {
  const w = parseFloat(weight) || 0;
  const u = (unit || '').toUpperCase();
  if (u === 'CARAT' || u === 'CARATS' || u === 'CT')  return w * 0.2;
  if (u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER') return 0;
  return w; // gram (default)
};

/**
 * Sum an array of stones' weights in grams (unit-aware).
 *
 * @param {Array<{weight, unit}>} stones
 * @returns {number} total weight in grams
 */
export const stonesTotalGrams = (stones = []) =>
  stones.reduce((sum, s) => sum + stoneWeightToGrams(s.weight, s.unit), 0);

/**
 * Return a human-readable unit label for a stone.
 * Prefers stone.unit if present; falls back to category heuristic.
 *
 * @param {{ unit?: string, category?: string, stone_category?: string }} stone
 * @returns {'ct'|'g'|'pcs'}
 */
export const stoneUnitLabel = (stone) => {
  const unit = (stone.unit || '').toUpperCase();
  if (unit === 'CARAT' || unit === 'CARATS' || unit === 'CT')  return 'ct';
  if (unit === 'GRAM' || unit === 'GRAMS' || unit === 'G')     return 'g';
  if (unit === 'NOS' || unit === 'PCS' || unit === 'PIECES' || unit === 'EACH' || unit === 'NUMBER') return 'pcs';
  // Fallback: use category name
  const cat = (stone.category || stone.stone_category || '').toLowerCase();
  if (cat === 'diamond') return 'ct';
  return 'g';
};

// ── Jewelry cost calculations ───────────────────────────────────────────────

/**
 * Calculate net weight = gross weight − total stone weight (in grams).
 *
 * @param {number|string} grossWeight
 * @param {Array}         stones  - array of stone objects with { weight, unit }
 * @returns {number}
 */
export const calcNetWeight = (grossWeight, stones = []) =>
  Math.max(0, (parseFloat(grossWeight) || 0) - stonesTotalGrams(stones));

/**
 * Calculate gold cost: netWeight × goldRate × purityFactor
 * goldRate must be INR per gram (24K rate).
 *
 * @param {number|string} netWeight   - net gold weight in grams
 * @param {number|string} goldRate    - INR per gram (24K)
 * @param {number|string} purity      - karat (18, 22, 24) or string ('18KT', …)
 * @returns {number}
 */
export const calcGoldCost = (netWeight, goldRate, purity) =>
  (parseFloat(netWeight) || 0) * (parseFloat(goldRate) || 0) * getGoldPurityFactor(purity);

/**
 * Calculate wastage amount: goldCost × (wastagePercent / 100)
 *
 * @param {number|string} goldCost
 * @param {number|string} wastagePercent
 * @returns {number}
 */
export const calcWastage = (goldCost, wastagePercent) =>
  (parseFloat(goldCost) || 0) * ((parseFloat(wastagePercent) || 0) / 100);

/**
 * Calculate making charges: netWeight × chargePerGram
 *
 * @param {number|string} netWeight
 * @param {number|string} chargePerGram
 * @returns {number}
 */
export const calcMakingCharges = (netWeight, chargePerGram) =>
  (parseFloat(netWeight) || 0) * (parseFloat(chargePerGram) || 0);

export const calculateJewelryTotalCost = (jewelry) => {
  const materialsCost = jewelry.materials?.reduce((sum, material) => sum + material.totalCost, 0) || 0;
  return materialsCost + (jewelry.laborCost || 0) + (jewelry.otherCosts || 0);
};

export const calculateJewelryProfit = (jewelry) => {
  const totalCost = calculateJewelryTotalCost(jewelry);
  return (jewelry.salePrice || 0) - totalCost;
};

export const calculateJewelryMarkup = (jewelry) => {
  const totalCost = calculateJewelryTotalCost(jewelry);
  return totalCost > 0 ? ((jewelry.salePrice - totalCost) / totalCost * 100) : 0;
};

export const calculateMaterialMarkup = (material) => {
  return material.costPrice > 0 ? ((material.salePrice - material.costPrice) / material.costPrice * 100) : 0;
};

export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const formatPercentage = (value, decimals = 1) => {
  return `${value.toFixed(decimals)}%`;
};